import type { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import { ExplainClassificationButton } from "components/waste/complianceEngine/ExplainClassificationButton";
import { WasteClassificationDialog } from "components/waste/complianceEngine/WasteClassificationDialog";
import { ShippingDocumentKind, WasteLinesUnifiedQuery } from "generated-graphql/graphql";

export type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

/**
 * Which manifest line the dialog explains. Every row of a stream shares its
 * description, so that alone wouldn't tell rows apart.
 */
export function explainClassificationSubtitle(
  row: Pick<WasteLineUnifiedRow, "manifestTrackingNumber" | "wasteLineNumber"> & {
    documentKind?: ShippingDocumentKind;
  }
): string | undefined {
  const paperName =
    row.documentKind === ShippingDocumentKind.BillOfLading ? "Bill of lading" : "Manifest";
  const parts = [
    row.manifestTrackingNumber ? `${paperName} ${row.manifestTrackingNumber}` : null,
    row.wasteLineNumber ? `Line #${row.wasteLineNumber}` : null,
  ].filter((part): part is string => part != null);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * Staff-only affordance column, appended last so it doesn't disturb the
 * customer-visible layout. Pulled out of ManifestLineItemsDataGrid.tsx so it
 * can be unit tested as a plain function without that file's fast-refresh
 * lint rule complaining about a non-component export living beside the
 * `ManifestLineItemsDataGrid` component.
 */
export const buildExplainClassificationColumn = (
  isStaff: boolean
): OmnisearchGridColDef<WasteLineUnifiedRow>[] =>
  isStaff
    ? [
        {
          field: "explainClassification",
          headerName: "",
          sortable: false,
          filterable: false,
          width: 56,
          align: "center",
          // Prevent row link wrapper — the button opens a dialog and has its
          // own click handler; nested inside the row's <a> it would fire the
          // link navigation on click and tear the dialog down immediately.
          linkable: false,
          renderCell: ({ row }: { row: WasteLineUnifiedRow }) => (
            <ExplainClassificationButton
              renderDialog={(onClose) => (
                <WasteClassificationDialog
                  open
                  line={row}
                  onClose={onClose}
                  subtitle={explainClassificationSubtitle(row)}
                />
              )}
            />
          ),
        },
      ]
    : [];
